import { useCallback, useEffect, useState } from "react";
import {
  bankFromCsv,
  formatCents,
  type BankImportResult,
  type BankListResponse,
  type BankTransactionInfo,
  type BankTransactionInput,
  type DocumentListResponse,
  type ExpenseListResponse,
  type MeResponse,
} from "@handvero/shared";
import { api, errorText } from "../api";
import { Message, Sheet, useUnauthorizedRedirect } from "./ui";
import "./customers.css";
import { bankFromCamt } from "./bankCamt";
import "./bank.css";

/*
 * Bankseite: Kontoumsätze aus der Online-Banking-Datei (CSV oder CAMT/XML) einlesen und Rechnungen oder Ausgaben zuordnen.
 * Eine Live-Verbindung zum Konto gibt es noch nicht; sie kann später auf dieser Seite dazukommen.
 */

const dateText = (d: string) => d.split("-").reverse().join(".");

export function BankView({ me }: { me: MeResponse }) {
  const onAuth = useUnauthorizedRedirect();
  const [only, setOnly] = useState<"open" | "all">("open");
  const [data, setData] = useState<BankListResponse | null>(null);
  const [error, setError] = useState("");
  const [importing, setImporting] = useState(false);
  const [matching, setMatching] = useState<BankTransactionInfo | null>(null);
  const manager = me.user.role !== "employee";

  const load = useCallback(() => {
    api
      .get<BankListResponse>(`/api/bank/transactions${only === "open" ? "?state=open" : ""}`)
      .then((d) => {
        setData(d);
        setError("");
      })
      .catch((err) => onAuth(err) || setError(errorText(err)));
  }, [only, onAuth]);
  useEffect(load, [load]);

  async function act(path: string, body: object) {
    try {
      await api.post(path, body);
      load();
    } catch (err) {
      if (!onAuth(err)) setError(errorText(err));
    }
  }

  if (!manager) return <p className="cu-empty">Die Bankumsätze sehen nur Inhaber und Admins.</p>;
  return (
    <section className="cu-panel">
      <div className="dc-sums">
        <div>
          <small>Eingänge (importiert)</small>
          <b>{formatCents(data?.incomeCents ?? 0)}</b>
        </div>
        <div>
          <small>Ausgänge (importiert)</small>
          <b>{formatCents(data?.outgoCents ?? 0)}</b>
        </div>
        <div className={data && data.openCount > 0 ? "warn" : undefined}>
          <small>Noch nicht zugeordnet</small>
          <b>{data?.openCount ?? 0}</b>
        </div>
      </div>
      <div className="cu-bar">
        <div className="cu-roles" role="tablist">
          <button className={only === "open" ? "on" : undefined} onClick={() => setOnly("open")}>
            Offen
          </button>
          <button className={only === "all" ? "on" : undefined} onClick={() => setOnly("all")}>
            Alle
          </button>
        </div>
        <button className="dbtn" onClick={() => setImporting(true)}>
          Kontoauszug einlesen
        </button>
      </div>
      <Message>{error}</Message>
      {data === null && !error && <p className="muted-s">Lädt …</p>}
      {data?.items.length === 0 && (
        <p className="cu-empty">
          {only === "open" ? "Nichts mehr zuzuordnen." : "Noch keine Umsätze. Laden Sie im Online-Banking Ihre Umsätze als CSV oder CAMT herunter und lesen Sie die Datei hier ein."}
        </p>
      )}
      {data && data.items.length > 0 && (
        <table className="cu-table bk-bank">
          <thead>
            <tr>
              <th>Datum</th>
              <th>Von / An</th>
              <th>Verwendungszweck</th>
              <th className="num">Betrag</th>
              <th>Zuordnung</th>
            </tr>
          </thead>
          <tbody>
            {data.items.map((t) => (
              <tr key={t.id} className={t.state === "ignored" ? "dim" : undefined}>
                <td>{dateText(t.bookingDate)}</td>
                <td>{t.counterparty ?? "–"}</td>
                <td className="bk-purpose">{t.purpose ?? ""}</td>
                <td className={`num ${t.amountCents < 0 ? "neg" : "pos"}`}>{formatCents(t.amountCents)}</td>
                <td>
                  {t.state === "matched" && <span className="dc-state paid">{t.matchedLabel}</span>}
                  {t.state === "ignored" && (
                    <button className="dbtn ghost small" onClick={() => void act(`/api/bank/transactions/${t.id}/ignore`, { ignored: false })}>
                      Wieder anzeigen
                    </button>
                  )}
                  {t.state === "open" && (
                    <span className="bk-actions">
                      {t.suggestion && (
                        <button
                          className="dbtn small"
                          title={t.suggestion.reason}
                          onClick={() => void act(`/api/bank/transactions/${t.id}/match`, t.suggestion!.kind === "invoice" ? { documentId: t.suggestion!.id } : { expenseId: t.suggestion!.id })}
                        >
                          {t.suggestion.label} zuordnen
                        </button>
                      )}
                      <button className="dbtn ghost small" onClick={() => setMatching(t)}>
                        {t.suggestion ? "Anders" : "Zuordnen"}
                      </button>
                      <button className="dbtn ghost small" onClick={() => void act(`/api/bank/transactions/${t.id}/ignore`, { ignored: true })}>
                        Ausblenden
                      </button>
                    </span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
      {importing && (
        <ImportSheet
          onClose={() => setImporting(false)}
          onDone={() => {
            setImporting(false);
            load();
          }}
        />
      )}
      {matching && (
        <MatchSheet
          tx={matching}
          onClose={() => setMatching(null)}
          onDone={() => {
            setMatching(null);
            load();
          }}
        />
      )}
    </section>
  );
}

function ImportSheet(props: { onClose: () => void; onDone: () => void }) {
  const [txs, setTxs] = useState<BankTransactionInput[] | null>(null);
  const [note, setNote] = useState("");
  const [fileName, setFileName] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const [result, setResult] = useState<BankImportResult | null>(null);

  async function readFile(file: File | undefined) {
    if (!file) return;
    setFileName(file.name);
    setResult(null);
    setError("");
    setTxs(null);
    try {
      const text = await file.text();
      if (/^\s*<\?xml|<Document[\s>]/.test(text.slice(0, 400))) {
        const r = bankFromCamt(text);
        setTxs(r.transactions);
        setNote(r.skipped ? `${r.skipped} Zeilen ohne Datum oder Betrag übersprungen.` : "");
      } else {
        const r = bankFromCsv(text);
        if (r.missing.length) setError(`In der Datei fehlt: ${r.missing.join(", ")}. Bitte laden Sie die Umsätze als CSV oder CAMT mit Spaltenüberschriften herunter.`);
        setTxs(r.transactions);
        setNote(r.skipped.length ? `${r.skipped.length} Zeilen übersprungen (${r.skipped.slice(0, 3).map((s) => `Zeile ${s.line}: ${s.reason}`).join(", ")}).` : "");
      }
    } catch {
      setError("Die Datei konnte nicht gelesen werden. Bitte laden Sie die Umsätze noch einmal als CSV oder CAMT herunter.");
    }
  }

  async function run() {
    if (!txs) return;
    setBusy(true);
    setError("");
    try {
      setResult(await api.post<BankImportResult>("/api/bank/import", { transactions: txs }));
    } catch (err) {
      setError(errorText(err));
    }
    setBusy(false);
  }

  return (
    <Sheet title="Kontoauszug einlesen" onClose={result ? props.onDone : props.onClose}>
      <div className="cu-form">
        <p className="muted-s wide">
          Laden Sie im Online-Banking Ihre Umsätze herunter (CSV oder CAMT/XML) und wählen Sie die Datei hier aus. Umsätze, die Sie schon einmal eingelesen haben, werden
          erkannt und nicht doppelt angelegt. Ihre Datei verlässt HandVero nicht.
        </p>
        <input className="wide" type="file" accept=".csv,.xml,.txt,text/csv,text/xml,application/xml,text/plain" onChange={(e) => void readFile(e.target.files?.[0])} />
        {txs && txs.length > 0 && (
          <div className="cu-preview wide">
            <p>
              <b>{fileName}</b>: {txs.length} Umsätze erkannt. {note}
            </p>
            <ul>
              {txs.slice(0, 5).map((t, i) => (
                <li key={i}>
                  {dateText(t.bookingDate)} · {t.counterparty ?? "–"} · {formatCents(t.amountCents)}
                </li>
              ))}
              {txs.length > 5 && <li>… und {txs.length - 5} weitere</li>}
            </ul>
            <button className="dbtn" disabled={busy || !!result} onClick={() => void run()}>
              {txs.length} Umsätze einlesen
            </button>
          </div>
        )}
        <div className="wide">
          <Message>{error}</Message>
          {result && (
            <p className="app-msg ok">
              {result.created} neue Umsätze eingelesen{result.duplicates ? `, ${result.duplicates} schon vorhanden` : ""}.
            </p>
          )}
        </div>
      </div>
    </Sheet>
  );
}

function MatchSheet(props: { tx: BankTransactionInfo; onClose: () => void; onDone: () => void }) {
  const incoming = props.tx.amountCents > 0;
  const [options, setOptions] = useState<{ id: string; label: string }[] | null>(null);
  const [pick, setPick] = useState("");
  const [error, setError] = useState("");

  useEffect(() => {
    if (incoming) {
      api
        .get<DocumentListResponse>("/api/documents?type=invoice")
        .then((r) => setOptions(r.items.filter((d) => d.status === "final" && d.state !== "paid" && d.state !== "cancelled").map((d) => ({ id: d.id, label: `${d.number} · ${d.customerName} · ${formatCents(d.grossCents - d.paidCents)} offen` }))))
        .catch((err) => setError(errorText(err)));
    } else {
      api
        .get<ExpenseListResponse>("/api/expenses")
        .then((r) => setOptions(r.items.filter((e) => !e.paidOn).map((e) => ({ id: e.id, label: `${dateText(e.expenseDate)} · ${e.supplier} · ${formatCents(e.amountCents)}` }))))
        .catch((err) => setError(errorText(err)));
    }
  }, [incoming]);

  async function save() {
    try {
      await api.post(`/api/bank/transactions/${props.tx.id}/match`, incoming ? { documentId: pick } : { expenseId: pick });
      props.onDone();
    } catch (err) {
      setError(errorText(err));
    }
  }

  return (
    <Sheet title={incoming ? "Rechnung zuordnen" : "Ausgabe zuordnen"} onClose={props.onClose}>
      <div className="cu-form">
        <p className="wide">
          {dateText(props.tx.bookingDate)} · {props.tx.counterparty ?? "–"} · <b>{formatCents(props.tx.amountCents)}</b>
        </p>
        {options === null && !error && <p className="muted-s wide">Lädt …</p>}
        {options && options.length === 0 && <p className="cu-empty wide">{incoming ? "Es gibt keine offene Rechnung." : "Es gibt keine Ausgabe ohne Zahlung. Legen Sie sie zuerst unter „Ausgaben“ an."}</p>}
        {options && options.length > 0 && (
          <label className="wide">
            {incoming ? "Offene Rechnung" : "Ausgabe"}
            <select value={pick} onChange={(e) => setPick(e.target.value)}>
              <option value="">Bitte wählen …</option>
              {options.map((o) => (
                <option key={o.id} value={o.id}>
                  {o.label}
                </option>
              ))}
            </select>
          </label>
        )}
        <div className="wide">
          <Message>{error}</Message>
        </div>
        <div className="cu-buttons wide">
          <button className="dbtn ghost" onClick={props.onClose}>
            Abbrechen
          </button>
          <button className="dbtn" disabled={!pick} onClick={() => void save()}>
            Zuordnen
          </button>
        </div>
      </div>
    </Sheet>
  );
}
